import type { Control, FieldPath, FieldValues } from 'react-hook-form';
import { useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form as AntdForm, Typography } from 'antd';
import type { ValidateStatus } from 'antd/es/form/FormItem';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { PosLabel } from './PosLabel.tsx';

type AntdFormItemProps = React.ComponentProps<typeof AntdForm.Item>;

type PosFormItemKeyProps = Pick<
  AntdFormItemProps,
  | 'required'
  | 'help'
  | 'label'
  | 'labelAlign'
  | 'labelCol'
  | 'noStyle'
  | 'colon'
  | 'valuePropName'
>;

type PosFormItemExtendedProps = Omit<
  AntdFormItemProps,
  | 'name'
  | 'normalize'
  | 'rules'
  | 'validateStatus'
  | 'children'
  | 'tooltip'
  | keyof PosFormItemKeyProps
>;

export type PosFormItemControlledComponentProps<
  TFieldValues extends FieldValues = FieldValues,
> = Omit<PosFormItemProps<TFieldValues>, 'children' | 'control' | 'name'>;

export type PosFormItemUncontrolledComponentProps = Partial<
  Record<keyof PosFormItemControlledComponentProps, never>
>;

/**
 * Props for {@link PosFormItem}
 */
export type PosFormItemProps<TFieldValues extends FieldValues = FieldValues> = {
  children: React.ReactNode;
  /** Control field from `useForm` hook @example
   * ```tsx
   * import { useForm } from 'react-hook-form';
   * const { control } = useForm<ExampleForm>();
   */
  control: Control<TFieldValues>;
  /** Input form field name */
  name: FieldPath<TFieldValues>;
  /** Override the field validation status. _(If undefined, it will be generated by the validation rule)_ */
  validateStatus?: ValidateStatus | undefined;
  /** Tooltip text to display on label hover _(hidden when empty)_ */
  tooltip?: string;
  /** Hides the margin placeholder for error message if field is valid / untouched */
  noMarginOnValid?: boolean;
  /** Displays bold label style */
  boldLabel?: boolean;
  /** Additional form item props.
   * @see {@link PosFormItemExtendedProps} */
  formItemProps?: PosFormItemExtendedProps;
} & PosFormItemKeyProps;

/**
 * @component
 * @description Form component wrapper for React-Hook-Form integration
 * For use on Ant-D form input components that haven't already been pre-wrapped or are unable to be
 * @see {@link https://ant.design/components/form#formitem | AntD FormItem Component}
 *
 * @param props {@link PosFormItemProps}
 * @example
 * ```tsx
 * import { trpc } from 'pos-client/utils/trpc.ts';
 * import { PosFormItem } from 'pos-client/components/shared/Form/PosFormItem.tsx';
 * import { PosSearchSelect } from 'pos-client/components/shared/Form/PosSearchSelect.tsx';
 * import { useForm } from 'react-hook-form';
 *
 * const { control } = useForm<CustomerForm>();
 *
 * <PosFormItem
 *   name='stockId'
 *   control={control}
 *   label='stockItem'
 * >
 *   <PosSearchSelect
 *     queryKey="searchStock"
 *     trpcQuery={trpc.searchStock}
 *     searchKey="searchText"
 *     resultsKey="results"
 *     queryInputs={{ pageNumber: 1, resultsPerPage: 100 }}
 *     optionNames={{
 *       label: 'sku',
 *       secondaryLabel: 'description',
 *       value: 'id',
 *     }}
 *    />
 * </PosFormItem>
 * ```
 */
export const PosFormItem = <TFieldValues extends FieldValues = FieldValues>({
  children,
  control,
  name,
  help,
  required,
  validateStatus,
  noMarginOnValid,
  boldLabel,
  tooltip,
  formItemProps,
  label,
  ...props
}: PosFormItemProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { field, fieldState } = useController({ name, control });
  const handleNormalize: AntdFormItemProps['normalize'] = (value) => {
    field.onChange(value);
    return value;
  };

  const RequiredMessageComponent = () => (
    <Typography.Text type="danger">
      *{t('common.formErrors.required')}
    </Typography.Text>
  );

  const renderHelp = () => {
    // Help message priority: 1. help prop, 2. error message, 3. required field, or undefined

    if (help) return help;

    const valueIsNull =
      isNil(field.value) ||
      (isNaN(Number(field.value)) && isEmpty(field.value));

    if (fieldState.error?.message) {
      const errorMessage = fieldState.error.message.trim().toLowerCase();

      if (
        errorMessage === 'required' ||
        errorMessage === 'mandatory field' ||
        (errorMessage === 'invalid day.js type' && valueIsNull) ||
        errorMessage === 'invalid'
      ) {
        return <RequiredMessageComponent />;
      }

      return fieldState.error.message;
    }

    if (required && valueIsNull) {
      return <RequiredMessageComponent />;
    }
  };

  const hideItemMargin = noMarginOnValid ? isNil(renderHelp()) : false;

  return (
    <AntdForm.Item
      name={name as string}
      initialValue={field.value}
      normalize={handleNormalize}
      validateStatus={
        (validateStatus ?? fieldState.invalid) ? 'error' : undefined
      }
      help={renderHelp()}
      required={required}
      label={
        !label && !tooltip ? null : (
          <PosLabel tooltip={tooltip} label={label} boldLabel={boldLabel} />
        )
      }
      tooltip={null}
      style={{
        marginBottom: hideItemMargin ? 0 : undefined,
        ...formItemProps?.style,
      }}
      {...props}
      {...formItemProps}
    >
      {children}
    </AntdForm.Item>
  );
};
